"use client";

import { useMemo, useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import { api } from "@/trpc/react";
import type { Notification } from "@/types/notifications";
import {
  loadToasts,
  loadPreferences,
  savePreferences,
  markToastAsRead as markToastAsReadStorage,
  clearToasts,
} from "@/lib/notificationStorage";

/**
 * Hook to manage notification state combining news and toasts
 */
export function useNotifications() {
  const { data: session } = useSession();
  const userId = session?.user?.id;

  // Load preferences from localStorage, re-derive when userId changes
  const initialShowRead = useMemo(() => {
    if (!userId) return false;
    return loadPreferences(userId).showRead;
  }, [userId]);

  const [showReadOverride, setShowReadOverride] = useState<boolean | null>(null);
  const [showReadUserId, setShowReadUserId] = useState(userId);

  // Reset override when userId changes so we re-read from localStorage
  if (userId !== showReadUserId) {
    setShowReadOverride(null);
    setShowReadUserId(userId);
  }

  const showRead = showReadOverride ?? initialShowRead;

  // Save preferences when they change
  const handleToggleShowRead = useCallback(
    (value: boolean) => {
      setShowReadOverride(value);
      if (userId) {
        savePreferences(userId, { showRead: value });
      }
    },
    [userId]
  );

  // Fetch unread news
  const { data: unreadNewsData, refetch: refetchUnread } = api.news.getUnread.useQuery(
    { limit: 10, offset: 0 },
    { enabled: !!userId }
  );

  // Fetch active news (if showing read)
  const { data: activeNewsData, isLoading: isActiveNewsLoading } = api.news.getActive.useQuery(
    { limit: 10, offset: 0 },
    { enabled: !!userId && showRead }
  );

  // Mark single news as read mutation
  const markNewsAsReadMutation = api.news.markAsRead.useMutation({
    onSuccess: () => {
      refetchUnread();
    },
  });

  // Mark all news as read mutation
  const markAllNewsAsReadMutation = api.news.markAllAsRead.useMutation({
    onSuccess: () => {
      refetchUnread();
    },
  });

  // Toast refresh trigger: incremented by polling and manual actions to re-derive toast state
  const [toastRefreshTrigger, setToastRefreshTrigger] = useState(0);

  // Derive toast notifications from localStorage, re-computed when userId or trigger changes
  const toastNotifications = useMemo((): Notification[] => {
    // toastRefreshTrigger is intentionally in scope to trigger re-computation
    void toastRefreshTrigger;
    if (!userId) return [];

    const storedToasts = loadToasts(userId);

    return storedToasts.map((toast) => ({
      id: toast.id,
      kind: "toast" as const,
      title: null,
      messagePreview: toast.message,
      createdAt: new Date(toast.createdAt),
      readAt: toast.readAt ? new Date(toast.readAt) : null,
      meta: {
        kind: "toast" as const,
        tone: toast.tone,
      },
    }));
  }, [userId, toastRefreshTrigger]);

  // Poll for new toasts every 2 seconds (to catch newly persisted toasts)
  useEffect(() => {
    if (!userId) return;

    const interval = setInterval(() => {
      setToastRefreshTrigger((prev) => prev + 1);
    }, 2000);

    return () => clearInterval(interval);
  }, [userId]);

  // Combine news and toasts
  const allNotifications = useMemo(() => {
    const notifications: Notification[] = [];

    // Add news announcements
    const newsToShow = showRead ? activeNewsData?.announcements : unreadNewsData?.announcements;

    if (newsToShow) {
      newsToShow.forEach((announcement) => {
        // Check if it's unread by looking at unread list
        const isUnread = unreadNewsData?.announcements.some((u) => u.id === announcement.id);

        // Get first 3 words from description as preview
        // Strip markdown images and links first
        const cleanContent = announcement.content
          .replace(/!\[.*?\]\(.*?\)/g, "") // Remove markdown images: ![alt](url)
          .replace(/\[([^\]]+)\]\(.*?\)/g, "$1") // Convert markdown links to text: [text](url) -> text
          .trim();

        const words = cleanContent.split(/\s+/).filter((w) => w.length > 0);
        const previewText = words.slice(0, 3).join(" ");
        const hasMore = words.length > 3;

        notifications.push({
          id: `news-${announcement.id}`,
          kind: "news",
          title: announcement.title,
          messagePreview: previewText + (hasMore ? "..." : ""),
          createdAt: new Date(announcement.publishedAt || announcement.createdAt),
          readAt: isUnread ? null : new Date(), // If not in unread list, mark as read
          meta: {
            kind: "news",
            announcementId: announcement.id,
            severity: announcement.severity as "info" | "warning" | "critical" | undefined,
          },
        });
      });
    }

    // Add toast notifications (limit to most recent 10 for the dropdown)
    const recentToasts = toastNotifications.slice(0, 10);
    notifications.push(...recentToasts);

    // Sort by createdAt descending (newest first)
    return notifications.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [unreadNewsData, activeNewsData, toastNotifications, showRead]);

  // Mark as read handler
  const handleMarkAsRead = useCallback(
    (id: string) => {
      if (!userId) return;

      // Check if it's a news notification
      if (id.startsWith("news-")) {
        const announcementId = id.replace("news-", "");
        markNewsAsReadMutation.mutate({ announcementId });
      }
      // Check if it's a toast notification
      else if (id.startsWith("toast-")) {
        markToastAsReadStorage(userId, id);
        // Trigger re-derivation of toast notifications
        setToastRefreshTrigger((prev) => prev + 1);
      }
    },
    [userId, markNewsAsReadMutation]
  );

  // Mark all as read handler
  const handleMarkAllAsRead = useCallback(() => {
    markAllNewsAsReadMutation.mutate();
  }, [markAllNewsAsReadMutation]);

  // Clear all toasts handler
  const handleClearToasts = useCallback(() => {
    if (!userId) return;
    clearToasts(userId);
    setToastRefreshTrigger((prev) => prev + 1);
  }, [userId]);

  return {
    notifications: allNotifications,
    showRead,
    onToggleShowRead: handleToggleShowRead,
    onMarkAsRead: handleMarkAsRead,
    onMarkAllAsRead: handleMarkAllAsRead,
    onClearToasts: handleClearToasts,
    isLoading: showRead && isActiveNewsLoading,
  };
}
